import React, { useState, useEffect } from 'react';
import {
  Box,
  HStack,
  VStack,
  Grid,
  GridItem,
  Select,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Text,
  Icon,
  useColorModeValue
} from '@chakra-ui/react';
import { CalendarIcon, EditIcon } from '@chakra-ui/icons';
import SearchableSelectChakra from './SearchableSelectChakra';
import { API_BASE_URL } from '../../../../utils/apiConfig';

const AgendaFilters = ({ filters, onFiltersChange }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter to show only users with Associado permission (or all based on your needs)
        const filteredUsers = data.data?.filter(user => 
          user.permissions?.includes('Associado') || 
          user.permissions?.includes('Gestor')
        ) || [];
        setUsers(filteredUsers);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleUserSelect = (userId) => {
    if (userId && !filters.userIds.includes(userId)) {
      onFiltersChange({
        ...filters,
        userIds: [...filters.userIds, userId]
      });
      setSelectedUser('');
    }
  };

  const handleRemoveUser = (userId) => {
    onFiltersChange({
      ...filters,
      userIds: filters.userIds.filter(id => id !== userId)
    });
  };

  const handleTypeChange = (type) => {
    if (type) {
      const newTypes = filters.types.includes(type) 
        ? filters.types.filter(t => t !== type)
        : [...filters.types, type];
      
      onFiltersChange({
        ...filters,
        types: newTypes
      });
      setSelectedType('');
    }
  };

  const handleStatusChange = (e) => {
    onFiltersChange({
      ...filters,
      status: e.target.value
    });
  };

  const getSelectedUserName = (userId) => {
    const user = users.find(u => u._id === userId);
    return user ? user.nomeCompleto : userId;
  };

  const eventTypes = [
    { value: 'appointment', label: 'Compromisso' },
    { value: 'meeting', label: 'Reunião' },
    { value: 'task', label: 'Tarefa' },
    { value: 'reminder', label: 'Lembrete' },
    { value: 'other', label: 'Outro' }
  ];

  const getTypeLabel = (type) => {
    const typeObj = eventTypes.find(t => t.value === type);
    return typeObj ? typeObj.label : type;
  };

  return (
    <Box 
      bg={bgColor} 
      p={5} 
      borderRadius="lg" 
      borderWidth="1px" 
      borderColor={borderColor}
      boxShadow="sm"
      transition="all 0.3s"
      _hover={{ boxShadow: "md" }}
    >
      <HStack mb={4}>
        <Icon as={CalendarIcon} color="green.500" boxSize={5} />
        <Text fontWeight="bold" fontSize="lg" color="gray.700">
          Filtros da Agenda
        </Text>
      </HStack>
      
      <Grid 
        templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} 
        gap={4}
        mb={4}
      >
        <GridItem>
          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              Usuários
            </Text>
            <SearchableSelectChakra
              options={users.filter(user => !filters.userIds.includes(user._id))}
              value={selectedUser}
              onChange={handleUserSelect}
              placeholder="Buscar usuário..."
              displayField={(user) => `${user.nomeCompleto} (${user.permissions.join(', ')})`}
              valueField="_id"
              size="sm"
            />
          </VStack>
        </GridItem>

        <GridItem>
          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              Tipo de Evento
            </Text>
            <SearchableSelectChakra
              options={eventTypes.filter(type => !filters.types.includes(type.value))}
              value={selectedType}
              onChange={(value) => handleTypeChange(value)}
              placeholder="Adicionar tipo..."
              displayField="label"
              valueField="value"
              size="sm"
            />
          </VStack>
        </GridItem>

        <GridItem>
          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              Status
            </Text>
            <Select
              value={filters.status}
              onChange={handleStatusChange}
              size="sm"
              borderRadius="md"
              _hover={{ borderColor: "gray.400" }}
              _focus={{ borderColor: "green.500", boxShadow: "0 0 0 1px green.500" }}
            >
              <option value="all">Todos</option>
              <option value="scheduled">Agendado</option>
              <option value="completed">Concluído</option>
              <option value="cancelled">Cancelado</option>
            </Select>
          </VStack>
        </GridItem>
      </Grid>

      {(filters.userIds.length > 0 || filters.types.length > 0) && (
        <VStack align="stretch" spacing={3}>
          <Box h="1px" bg="gray.200" />
          
          {filters.userIds.length > 0 && (
            <Box>
              <Text fontSize="sm" color="gray.600" mb={2} fontWeight="medium">
                Usuários selecionados:
              </Text>
              <Wrap spacing={2}>
                {filters.userIds.map(userId => (
                  <WrapItem key={userId}>
                    <Tag 
                      size="md" 
                      colorScheme="purple" 
                      borderRadius="full"
                      variant="subtle"
                    >
                      <TagLabel>{getSelectedUserName(userId)}</TagLabel>
                      <TagCloseButton onClick={() => handleRemoveUser(userId)} />
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          )}

          {filters.types.length > 0 && (
            <Box>
              <Text fontSize="sm" color="gray.600" mb={2} fontWeight="medium">
                Tipos selecionados:
              </Text>
              <Wrap spacing={2}>
                {filters.types.map(type => (
                  <WrapItem key={type}>
                    <Tag 
                      size="md" 
                      colorScheme="blue" 
                      borderRadius="full"
                      variant="subtle"
                    >
                      <TagLabel>{getTypeLabel(type)}</TagLabel>
                      <TagCloseButton onClick={() => handleTypeChange(type)} />
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          )}
        </VStack>
      )}
    </Box>
  );
};

export default AgendaFilters;